<!-- 
  @component ❌
  @deprecated Use the `<Dialog />`, `<Drawer />` or `<ConfirmDialog />` components from `$mds/Dialogs` instead.  
  @file apps/web/src/components/mds/Dialogs/Dialog.svelte
  @ignore
-->
<svelte:options immutable={true} />

<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy, afterUpdate } from 'svelte';
  import { fly, fade, scale } from 'svelte/transition';
  import { cubicInOut } from 'svelte/easing';
  import { browser } from '$app/environment';
  import { toggleBodyScroll } from '$lib/utils';
  import { BREAKPOINTS } from '$lib/constants';
  import { swipe } from 'svelte-gestures';

  import { portal } from '$components/Portal.svelte';
  import Button from '$components/Button.svelte';

  import FontAwesomeIcon from '$components/FontAwesomeIcon.svelte';
  import { faXmark } from '@fortawesome/pro-solid-svg-icons';

  const dispatch = createEventDispatcher();

  export let isVisible = false;
  /** @type {'center' | 'right' | 'bottom' | bottom-mobile | 'bottom-tablet'} */
  export let placement = 'center';
  export let widthClass = 'w-11/12 md:w-7/12 xl:w-9/12';
  export let wrapperClass = 'fixed z-modal';
  export let backgroundClass = 'bg-ondark-overlay';
  export let backgroundZindex = 'z-modal-backdrop';
  export let closeButtonPosition = 'right-6 top-6';
  export let closeButtonClass = '';
  export let modalZindex = 'z-modal';
  export let portalTarget: string | null = null;
  export let limitHeight = true;
  export let closeButton = true;
  export let clientHeight = 0;
  export let closeOnClickOutside = true;
  export let closeOnEscape = true;
  let className = 'bg-ondark-low shadow';
  export { className as class };
  export let overflow = 'overflow-auto';
  export let rounded = 'rounded-3xl';
  export let ariaLabel = 'modal';
  export let disableScroll: boolean = true;
  export let useCloseAsBack = false;
  export let transition: 'fly' | 'scale' | 'fly-up' = 'fly';
  export let position = 'relative';

  let innerWidth;
  export let el: HTMLElement | undefined = undefined;

  $: if (isVisible && disableScroll) {
    onOpen();
  } else {
    dispatch('close');
    // Re-enable body scrolling after all modals are closed
    if (browser && document.querySelectorAll('.modal').length <= 1) {
      toggleBodyScroll(true);
    }
  }

  function onOpen() {
    // Prevent body scrolling when modal is open
    toggleBodyScroll(false);
    dispatch('open', { el });
  }

  function onKeyDown(event) {
    // Close on escape key
    if (!closeOnEscape || event.isComposing || event.keyCode !== 27) {
      return;
    }

    close();
  }

  function close() {
    if (useCloseAsBack) {
      dispatch('back');
      return;
    }
    isVisible = false;
  }

  function getTransition(node, args) {
    if (transition === 'fly-up') {
      return fly(node, { duration: 400, easing: cubicInOut, ...(args.in ? { y: 1000 } : { y: 50 }) });
    }
    if (transition === 'scale') {
      return scale(node, { start: 0.95, duration: 300, easing: cubicInOut });
    }
    switch (placement) {
      case 'center':
      case 'bottom':
        return fly(node, { duration: 400, easing: cubicInOut, ...(args.in ? { y: -50 } : { y: 50 }) });
      case 'bottom-mobile':
        return innerWidth < BREAKPOINTS.sm
          ? fly(node, { duration: 400, easing: cubicInOut, y: 50 })
          : fly(node, { duration: 400, easing: cubicInOut, ...(args.in ? { y: -50 } : { y: 50 }) });
      case 'bottom-tablet':
        return innerWidth < BREAKPOINTS.md
          ? fly(node, { duration: 400, easing: cubicInOut, y: 50 })
          : fly(node, { duration: 400, easing: cubicInOut, ...(args.in ? { y: -50 } : { y: 50 }) });
      default:
        return fly(node, { duration: 400, easing: cubicInOut, x: 50 });
    }
  }

  onMount(() => {
    if (isVisible) {
      toggleBodyScroll(false);
    }
  });

  afterUpdate(() => {
    if (isVisible) {
      dispatch('afterVisible');
    }
  });

  onDestroy(() => {
    toggleBodyScroll(true);
  });
  function handleSwipe(event) {
    if (event.detail.direction === 'bottom') {
      close();
    }
  }
</script>

<svelte:window on:keydown={onKeyDown} bind:innerWidth />

<!-- Sometimes we need to use a portal to render the Modal outside of its parent component
  For example, when the Modal is a child of an element that has the `transform` property set, it breaks the modal behaviour. -->
{#if isVisible}
  <div
    bind:clientHeight
    class="{wrapperClass} inset-0 bottom-[env(safe-area-inset-bottom)] flex"
    use:portal={portalTarget}
  >
    <!-- Background -->
    <div
      class="fixed left-0 top-0 block h-full w-full {backgroundClass} {backgroundZindex}"
      transition:fade|local={{ duration: 150 }}
      on:click={() => {
        dispatch('clickoutside');

        if (closeOnClickOutside) {
          close();
        }
      }}
      use:swipe={{ timeframe: 500, minSwipeDistance: 60 }}
      on:swipe={() => handleSwipe(event)}
    />
    <!-- Modal -->
    <div
      bind:this={el}
      class="modal {position} {modalZindex} {className} {overflow} {widthClass} {rounded}"
      class:bottom={placement === 'bottom'}
      class:bottom-mobile={placement === 'bottom-mobile'}
      class:bottom-tablet={placement === 'bottom-tablet'}
      class:center={placement === 'center'}
      class:right={placement === 'right'}
      class:limitHeight
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      in:getTransition|local={{ in: true }}
      out:getTransition|local={{ in: false }}
      on:outroend={() => {
        if (document.querySelectorAll('.modal').length <= 1) {
          toggleBodyScroll(true);
        }
      }}
    >
      {#if placement === 'bottom-mobile'}
        <span class="bg-ondark-high z-1 absolute left-0 right-0 top-2 mx-auto block h-1 w-10 rounded sm:hidden" />
      {/if}
      <!-- Close button -->
      {#if closeButton}
        <Button
          kind="secondary"
          size="w-10 h-10"
          padding=""
          class="absolute {closeButtonPosition} bg-ondark-low z-20 flex rounded {closeButtonClass}"
          on:click={close}
        >
          <FontAwesomeIcon icon={faXmark} class="m-auto h-4 w-4" />
        </Button>
      {/if}

      <!-- Content -->
      <slot />
    </div>
  </div>
{/if}

<style lang="postcss">
  .modal {
    &.bottom {
      margin-top: auto;
    }
    &.bottom-mobile {
      margin-top: auto;

      @screen sm {
        margin: auto;
        &.limitHeight {
          max-height: calc(100vh - 4rem);
        }
      }
    }
    &.bottom-tablet {
      margin-top: auto;

      @screen md {
        margin: auto;
        &.limitHeight {
          max-height: calc(100vh - 4rem);
        }
      }
    }
    &.center {
      margin: auto;
      &.limitHeight {
        max-height: calc(100vh - 4rem);
      }
    }
    &.right {
      margin-left: auto;
      height: 100%;
    }
  }
</style>
